import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, tap } from 'rxjs/operators';
import { CompanyModel, CompanySearchModel, CustomerListModel, CustomerModel, CustomerUserModel, SendCredentialsRequest } from "src/app/models/customer/customer.model";
import { CustomerDashboardModel, CustomerProfileModel, CustomerTankAlertsModel, CustomerTankModel, CustomerTankPropaneLevelModel, CustomerTankUsageLevelModel, CustomerTankUsagesLevelModel } from "src/app/models/customer/customerDashboard.model";


@Injectable({
    providedIn: 'root'
})
export class CustomerService {

    private CUSTOMERS_URL = 'api/customer/getCustomers/'
    private CUSTOMERS_TANK_URL = 'api/customer/getTankCustomers/'
    private CUSTOMERS_BY_DIST_URL = 'api/customer/getCustomersByDistId/'
    private COMPANIES_BY_DIST_URL = 'api/customer/getCompaniesByDistId/'
    private COMPANY_LOOKUP = 'api/customer/companyLookup/'
    private COMPANY_BY_ID_URL = 'api/customer/getCompanyById/'
    private CUSTOMER_BY_ID_URL = 'api/customer/getCustomerById/'
    private CUSTOMER_DASHBOARD_BY_ID_URL = 'api/customer/getDashboardDataById/'
    private CUSTOMER_DASHBOARD_BY_TANKID_URL = 'api/customer/getDashboardByTankId/'
    private CUSTOMER_PROFILE_URL = 'api/customer/getCustomerProfile/'
    private CUSTOMER_ALERTS_URL = 'api/customer/getCustomerTankAlerts/'
    private CUSTOMER_PROPLEVELS_BY_DATERANGE_URL = 'api/customer/getPropaneLevelsByDateRange/'
    private CUSTOMER_USAGELEVELS_BY_DATERANGE_URL = 'api/customer/getUsageLevelsByDateRange/'
    private CUSTOMER_USAGESLEVELS_BY_DATERANGE_URL = 'api/customer/getUsagesLevelsByDateRange/'
    private CUSTOMER_TANKS_URL = 'api/customer/getCustomerTanksById/'
    private CUSTOMER_TANK_BY_ID_URL = 'api/customer/getCustomerTankById/'
    private CUSTOMER_LIST_BY_DIST_URL = 'api/customer/getCustomerListByDistId/'
    private CUSTOMER_SAVE = 'api/customer/saveCustomer'
    private COMPANY_SAVE = 'api/customer/saveCompany'
    private CUSTOMER_PROFILE_SAVE = 'api/customer/saveCustomerProfile'
    

    constructor(
        private _httpClient: HttpClient
    ) {
    }


    getCustomers$(id: number, tankId: number): Observable<CustomerModel[]> {
        return this._httpClient.get<CustomerModel[]>(`${this.CUSTOMERS_URL}${id}/${tankId}`); 
    }

    getTankCustomers$(id: number, tankId: number): Observable<CustomerModel[]> {
        return this._httpClient.get<CustomerModel[]>(`${this.CUSTOMERS_TANK_URL}${id}/${tankId}`); 
    }

    getCustomersByDistId$(id: string): Observable<CustomerModel[]> {
        return this._httpClient.get<CustomerModel[]>(`${this.CUSTOMERS_BY_DIST_URL}${id}`);
    }

    getCompaniesByDistId$(id: string): Observable<CustomerModel[]> {
        return this._httpClient.get<CustomerModel[]>(`${this.COMPANIES_BY_DIST_URL}${id}`);
    }

    companyLookup$(searchText: string, id: number): Observable<CompanySearchModel[]> {
        return this._httpClient.get<CompanySearchModel[]>(`${this.COMPANY_LOOKUP}${searchText}/${id}`);
    } 

    getCompanyById$(id: number): Observable<CompanyModel> {
        return this._httpClient.get<CompanyModel>(`${this.COMPANY_BY_ID_URL}${id}`)
    }

    getCustomerListByDistId$(id: string): Observable<CustomerListModel[]> {
        return this._httpClient.get<CustomerListModel[]>(`${this.CUSTOMER_LIST_BY_DIST_URL}${id}`);
    }

    getNewCustomer(distributorId: number): Observable<CustomerUserModel> {
        return this._httpClient.get<CustomerUserModel>(`/api/customer/new/${distributorId}`);
    }

    getCustomerUserById$(id: number): Observable<CustomerUserModel> {
        return this._httpClient.get<CustomerUserModel>(`${this.CUSTOMER_BY_ID_URL}${id}`);
    }

    getCustomerDashboardById$(id: number): Observable<CustomerDashboardModel> {
        return this._httpClient.get<CustomerDashboardModel>(`${this.CUSTOMER_DASHBOARD_BY_ID_URL}${id}`)
    }

    getDashboardByTankId$(id: number): Observable<CustomerDashboardModel> {
        return this._httpClient.get<CustomerDashboardModel>(`${this.CUSTOMER_DASHBOARD_BY_TANKID_URL}${id}`)
    }

    getCustomerProfile$(id: number): Observable<CustomerProfileModel> {
        return this._httpClient.get<CustomerProfileModel>(`${this.CUSTOMER_PROFILE_URL}${id}`)
    }

    getCustomerTankAlerts$(id: number): Observable<CustomerTankAlertsModel> {
        return this._httpClient.get<CustomerTankAlertsModel>(`${this.CUSTOMER_ALERTS_URL}${id}`)
    }

    getCustomerTanks$(id: number): Observable<CustomerTankModel[]> {
        return this._httpClient.get<CustomerTankModel[]>(`${this.CUSTOMER_TANKS_URL}${id}`)
    }

    getCustomerTankById$(id: number): Observable<CustomerTankModel> {
        return this._httpClient.get<CustomerTankModel>(`${this.CUSTOMER_TANK_BY_ID_URL}${id}`);
    }

    getUsagesLevelsByDateRange$(id: number, startDate: string, endDate: string): Observable<CustomerTankUsagesLevelModel> {
        return this._httpClient.get<CustomerTankUsagesLevelModel>(`${this.CUSTOMER_USAGESLEVELS_BY_DATERANGE_URL}${id}/${startDate}/${endDate}`);
    }

    sendCredentials(request: SendCredentialsRequest): Observable<any> {
        return this._httpClient.post('/api/customer/send-credentials', request);
    }

    saveCustomer$(customerUserModel: CustomerUserModel): Observable<CustomerUserModel | null> {
        return this._httpClient.post<CustomerUserModel>(this.CUSTOMER_SAVE, { CustomerUserResponseModel: customerUserModel }).pipe(
            tap(response => {

            }),
            catchError(error => {
                return of(error.error || 'Unknown error');
            }),
        );
    }

    saveCustomerProfile$(customerProfileModel: CustomerProfileModel): Observable<CustomerProfileModel | null> {
        return this._httpClient.post<CustomerProfileModel>(this.CUSTOMER_PROFILE_SAVE, { CustomerProfile: customerProfileModel }).pipe(
            tap(response => {

            }),
            catchError(error => {
                console.log(error);
                return of(null);
            }),
        );
    }

    saveCompany$(companyModel: CompanyModel): Observable<CompanyModel | null> {
        return this._httpClient.post<CompanyModel>(this.COMPANY_SAVE, { CompanyModel: companyModel }).pipe(
            tap(response => {

            }),
            catchError(error => {
                console.log(error);
                return of(null);
            }),
        );
    }


}
